/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { useCallback, useRef, useEffect, useState } from 'react';

import Button from '@atlaskit/button/default/button';
import { cssMap, cx, jsx } from '@atlaskit/css';
import Heading from '@atlaskit/heading/heading';
import CrossIcon from '@atlaskit/icon/core/cross';
import { Box } from '@atlaskit/primitives/compiled/box';
import { Flex } from '@atlaskit/primitives/compiled/flex';
import { Text } from '@atlaskit/primitives/compiled/text';
import { token } from '@atlaskit/tokens';

import {
	LabsCyphieMode,
	useReportLabsCyphieMessage,
} from '../../../common/analytics/LabsSessionTracker';
import { useAsideController, type Message } from '../../contexts/AsideContext';
import type { CyphieMode } from '../../state/LabsAtoms';
import { QueryWorkbenchFloatingPanelFrame } from '../QueryWorkbench';
import { ChatInput } from './ChatInput';
import { ChatMessage } from './ChatMessage';
import { useNL2Cypher } from './useNL2Cypher';

const styles = cssMap({
	container: {
		display: 'flex',
		flexDirection: 'column',
		height: '100%',
		backgroundColor: token('elevation.surface'),
		borderLeftWidth: token('border.width'),
		borderLeftStyle: 'solid',
		borderLeftColor: token('color.border'),
		borderTopLeftRadius: token('radius.large'),
	},
	containerWorkbench: {
		borderLeftWidth: 0,
		borderTopLeftRadius: 0,
	},
	header: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		paddingBlock: token('space.150'),
		paddingInline: token('space.200'),
		borderBottomWidth: token('border.width'),
		borderBottomStyle: 'solid',
		borderBottomColor: token('color.border'),
	},
	messagesContainer: {
		flexGrow: 1,
		overflowY: 'auto',
		paddingBlock: token('space.200'),
		paddingInline: token('space.200'),
		display: 'flex',
		flexDirection: 'column',
		gap: token('space.200'),
	},
	emptyState: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		justifyContent: 'center',
		height: '100%',
		textAlign: 'center',
		paddingInline: token('space.400'),
	},
});

export const ChatAside = ({
	appearance = 'default',
}: {
	appearance?: 'default' | 'workbench';
}): JSX.Element => {
	const { close, closeIfOwned, messages, addMessage, owner } = useAsideController();
	const { sendMessage, isLoading } = useNL2Cypher();
	const reportCyphieMessage = useReportLabsCyphieMessage();
	const messagesEndRef = useRef<HTMLDivElement>(null);
	const hasInitialized = useRef(false);
	const [selectedMode, setSelectedMode] = useState<CyphieMode>('default');

	// Auto-scroll to bottom when new messages arrive
	useEffect(() => {
		messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
	}, [messages]);

	useEffect(() => {
		if (messages.length === 0 && !hasInitialized.current) {
			hasInitialized.current = true;
			addMessage({
				id: 'welcome-message',
				role: 'assistant',
				content:
					"Hi! I'm Cyphie 👋 I'm here to help you write performant cypher queries for teamwork graph!\n\nYou can describe the type of cypher query you want, or send what you have now and we can iterate from there.",
			});
		}
	}, [messages.length, addMessage]);

	const handleSend = useCallback(
		async (content: string) => {
			const userMessage: Message = {
				id: `user-${Date.now()}`,
				role: 'user',
				content,
			};
			addMessage(userMessage);
			reportCyphieMessage(
				selectedMode === 'reasoning' ? LabsCyphieMode.Reasoning : LabsCyphieMode.Fast,
			);

			const result = await sendMessage(content, [...messages, userMessage], selectedMode);

			addMessage({
				id: `assistant-${Date.now()}`,
				role: 'assistant',
				content: result.content,
				metadata: {
					...(result.rawCypherQuery ? { rawCypherQuery: result.rawCypherQuery } : {}),
					cyphieMode: selectedMode,
				},
			});
		},
		[addMessage, reportCyphieMessage, sendMessage, messages, selectedMode],
	);

	const content = (
		<Box
			id={appearance === 'workbench' ? 'twg-labs-cyphie-panel' : undefined}
			xcss={cx(styles.container, appearance === 'workbench' && styles.containerWorkbench)}
		>
			<Flex xcss={styles.header}>
				<Heading size="small">Cyphie</Heading>
				<Button
					iconBefore={(iconProps) => <CrossIcon {...iconProps} />}
					appearance="subtle"
					onClick={() => (owner ? closeIfOwned(owner) : close())}
					aria-label="Close chat"
				>
					{' '}
				</Button>
			</Flex>

			<Box
				xcss={styles.messagesContainer}
				role="log"
				aria-label="Chat conversation"
				aria-relevant="additions text"
			>
				{messages.length === 0 ? (
					<Box xcss={styles.emptyState}>
						<Text color="color.text.subtlest">Ask a question or describe what you want to do.</Text>
					</Box>
				) : (
					messages.map((msg) => (
						<ChatMessage
							key={msg.id}
							messageRole={msg.role}
							content={msg.content}
							rawCypherQuery={msg.metadata?.rawCypherQuery}
							cyphieMode={msg.metadata?.cyphieMode}
							assistantName="Cyphie"
							showFeedback={msg.role === 'assistant' && msg.id !== 'welcome-message'}
						/>
					))
				)}
				{isLoading && (
					<ChatMessage
						messageRole="assistant"
						content="Thinking..."
						isLoading
						assistantName="Cyphie"
					/>
				)}
				<div ref={messagesEndRef} />
			</Box>

			<ChatInput
				onSend={handleSend}
				disabled={isLoading}
				selectedMode={selectedMode}
				onModeChange={setSelectedMode}
			/>
		</Box>
	);

	return appearance === 'workbench' ? (
		<QueryWorkbenchFloatingPanelFrame>{content}</QueryWorkbenchFloatingPanelFrame>
	) : (
		content
	);
};

export default ChatAside;
